import DateService, { FMT, DayjsUnit } from '@/helpers/DateService';
import { normalizeId, sameId } from '@/helpers/IdHelper';
const addToIndex = (index, key, guardia) => {
    if (key === null)
        return;
    const rows = index.get(key);
    if (rows) {
        rows.push(guardia);
    }
    else {
        index.set(key, [guardia]);
    }
};
export const buildGuardIndex = (medioPersonal) => {
    const byMedio = new Map();
    const byPersonal = new Map();
    const byDay = new Map();
    for (const guardia of medioPersonal) {
        addToIndex(byMedio, normalizeId(guardia.ID_MEDIO), guardia);
        addToIndex(byPersonal, normalizeId(guardia.ID_PERSONAL), guardia);
        const day = DateService.toDayjs(guardia.FECHA);
        if (day.isValid())
            addToIndex(byDay, day.format(FMT.dbDate), guardia);
    }
    return { byMedio, byPersonal, byDay, memo: new Map() };
};
export const matchesGuardForDateRecurso = (guardia, date, medio, personal) => {
    const entrada = DateService.toDayjs(guardia.FECHA_ENTRADA);
    const salida = guardia.FECHA_SALIDA ? DateService.toDayjs(guardia.FECHA_SALIDA) : null;
    const afterEntrada = date.isAfter(entrada) || date.isSame(entrada, DayjsUnit.Day);
    const beforeSalida = !salida || date.isBefore(salida) || date.isSame(salida, DayjsUnit.Day);
    if (!medio?.GUARDIAS_DINAMICAS && sameId(guardia.ID_MEDIO, medio?.ID_MEDIO)) {
        return afterEntrada && beforeSalida;
    }
    if (personal && sameId(guardia.ID_PERSONAL, personal.ID_PERSONAL)) {
        return afterEntrada && beforeSalida;
    }
    const sameDate = DateService.toDayjs(guardia.FECHA).isSame(date, DayjsUnit.Day);
    const sameMedio = sameId(guardia.ID_MEDIO, medio?.ID_MEDIO);
    const samePersonal = Boolean(guardia.ID_PERSONAL) &&
        (sameId(guardia.ID_PERSONAL, personal?.ID_PERSONAL) || Boolean(guardia.NUMERO_COMPONENTES));
    return sameDate && (sameMedio || samePersonal);
};
const isDirectMatch = (row, medioId, personalId) => (medioId !== null && normalizeId(row.ID_MEDIO) === medioId) ||
    (personalId !== null && normalizeId(row.ID_PERSONAL) === personalId);
export const compareGuardPriority = (a, b, medioId, personalId) => {
    const directA = isDirectMatch(a, medioId, personalId);
    const directB = isDirectMatch(b, medioId, personalId);
    if (directA !== directB)
        return directA ? -1 : 1;
    const entradaA = DateService.toDayjs(a.FECHA_ENTRADA);
    const entradaB = DateService.toDayjs(b.FECHA_ENTRADA);
    if (!entradaA.isSame(entradaB))
        return entradaA.isAfter(entradaB) ? -1 : 1;
    const idA = normalizeId(a.ID_MEDIO_PERSONAL) ?? '';
    const idB = normalizeId(b.ID_MEDIO_PERSONAL) ?? '';
    if (idA < idB)
        return -1;
    if (idA > idB)
        return 1;
    return 0;
};
const collectCandidates = (index, localDay, medioId, personalId) => {
    const seen = new Map();
    const addAll = (rows) => {
        if (!rows)
            return;
        for (const row of rows) {
            const key = normalizeId(row.ID_MEDIO_PERSONAL) ?? `${row.ID_MEDIO ?? ''}|${row.ID_PERSONAL ?? ''}|${row.FECHA}`;
            if (!seen.has(key))
                seen.set(key, row);
        }
    };
    addAll(medioId ? index.byMedio.get(medioId) : undefined);
    addAll(personalId ? index.byPersonal.get(personalId) : undefined);
    addAll(index.byDay.get(localDay));
    return Array.from(seen.values());
};
const pickWinner = (matches, medioId, personalId) => matches.length
    ? matches.reduce((best, current) => (compareGuardPriority(current, best, medioId, personalId) < 0 ? current : best))
    : undefined;
export const findGuardForDateRecurso = (index, date, medio, personal) => {
    if (!date?.isValid())
        return undefined;
    const medioId = normalizeId(medio?.ID_MEDIO);
    const personalId = normalizeId(personal?.ID_PERSONAL);
    const localDay = date.format(FMT.dbDate);
    const memoKey = `${localDay}|${medioId ?? ''}|${Boolean(medio?.GUARDIAS_DINAMICAS)}|${personalId ?? ''}`;
    if (index.memo.has(memoKey))
        return index.memo.get(memoKey);
    const candidates = collectCandidates(index, localDay, medioId, personalId);
    const matches = candidates.filter((guardia) => matchesGuardForDateRecurso(guardia, date, medio, personal));
    const winner = pickWinner(matches, medioId, personalId);
    index.memo.set(memoKey, winner);
    return winner;
};
export const findGuardsActiveOnDay = (index, day, isDynamic) => {
    const result = [];
    index.byMedio.forEach((rows, medioId) => {
        const medio = { ID_MEDIO: medioId, GUARDIAS_DINAMICAS: isDynamic(medioId) };
        const matches = rows.filter((guardia) => matchesGuardForDateRecurso(guardia, day, medio, null));
        const winner = pickWinner(matches, medioId, null);
        if (winner)
            result.push(winner);
    });
    return result;
};
